import React, { useState, useEffect } from 'react';
import { useAppSelector } from '../../hooks/useAppSelector';
import { API_ENDPOINTS } from '../../config/api';
import axios from '../../config/axiosConfig';
import Toast from '../Toast';
import LoadingOverlay from '../LoadingOverlay';

const StaffPerformance = () => {
    const { user, restaurant } = useAppSelector(state => state.auth);
    const restaurantId = restaurant?._id || user?.id;
    
    const [performanceData, setPerformanceData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedStaff, setSelectedStaff] = useState(null);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
    const [timeFilter, setTimeFilter] = useState('month'); // 'today', 'week', 'month'
    const [searchQuery, setSearchQuery] = useState('');
    const [roleFilter, setRoleFilter] = useState(''); // '' = all, 'WAITER', 'COOK', etc.

    useEffect(() => {
        if (restaurantId) {
            fetchStaffPerformance();
        }
    }, [restaurantId, timeFilter]);

    const fetchStaffPerformance = async () => {
        try {
            setLoading(true);
            const response = await axios.get(API_ENDPOINTS.STAFF_PERFORMANCE(restaurantId), {
                params: { filter: timeFilter }
            });
            setPerformanceData(response.data);
        } catch (error) {
            showToast('Failed to fetch staff performance data', 'error');
            console.error('Error fetching staff performance:', error);
        } finally {
            setLoading(false);
        }
    };

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 3000);
    };

    const formatDate = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric'
        });
    };

    const formatTime = (date) => {
        if (!date) return 'N/A';
        return new Date(date).toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getRatingStars = (rating) => {
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);
        
        return (
            <div className="flex items-center gap-0.5">
                {[...Array(fullStars)].map((_, i) => (
                    <span key={i} className="text-yellow-400 text-xs">★</span>
                ))}
                {hasHalfStar && <span className="text-yellow-400 text-xs">½</span>}
                {[...Array(emptyStars)].map((_, i) => (
                    <span key={i} className="text-gray-300 text-xs">★</span>
                ))}
                <span className="ml-1 text-[10px] text-stone-500">({rating.toFixed(1)})</span>
            </div>
        );
    };

    const uniqueRoles = [...new Set(performanceData.map((d) => (d.staff?.role || '').trim()).filter(Boolean))].sort();

    const filteredData = performanceData.filter((data) => {
        const name = (data.staff?.name || '').toLowerCase();
        const email = (data.staff?.email || '').toLowerCase();
        const role = (data.staff?.role || '').toLowerCase();
        const q = searchQuery.trim().toLowerCase();
        const searchMatch = !q || name.includes(q) || email.includes(q) || role.includes(q);
        const roleMatch = !roleFilter || role === roleFilter.toLowerCase();
        return searchMatch && roleMatch;
    });

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-orange-400 border-t-transparent" />
            </div>
        );
    }

    return (
        <div className="overflow-x-hidden min-w-0">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                <h2 className="text-base font-bold text-stone-800">Performance</h2>
                <div className="flex flex-wrap items-center gap-2">
                    <input
                        type="text"
                        placeholder="Search..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="px-2.5 py-2 text-xs border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400 focus:border-orange-400 outline-none min-w-[140px]"
                    />
                    <select
                        value={roleFilter}
                        onChange={(e) => setRoleFilter(e.target.value)}
                        className="px-2.5 py-2 text-xs border border-stone-200 rounded-xl focus:ring-2 focus:ring-orange-400 outline-none bg-white min-w-[100px]"
                    >
                        <option value="">All roles</option>
                        {uniqueRoles.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <div className="flex gap-1">
                        {['today', 'week', 'month'].map((period) => (
                            <button
                                key={period}
                                type="button"
                                onClick={() => setTimeFilter(period)}
                                className={`px-2.5 py-2 rounded-xl text-xs font-semibold capitalize transition-all ${timeFilter === period ? 'bg-orange-500 text-white shadow-md' : 'bg-white text-stone-600 border border-stone-200 hover:bg-stone-50'}`}
                            >
                                {period}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {performanceData.length === 0 ? (
                <div className="text-center py-8 text-stone-500 text-xs">No staff performance data available</div>
            ) : filteredData.length === 0 ? (
                <div className="text-center py-8 text-stone-500 text-xs">No staff match &quot;{searchQuery}&quot;</div>
            ) : (
                <div className="space-y-2">
                    {filteredData.map((data) => (
                        <div key={data.staff._id} className="bg-white rounded-lg border border-stone-200 shadow-sm p-2 sm:p-2.5 border-l-4 border-l-orange-400">
                            <div className="flex flex-wrap items-start justify-between gap-1.5 mb-2">
                                <div className="flex items-center gap-1.5 sm:gap-2 min-w-0 flex-1">
                                    {data.staff.photoUrl ? (
                                        <img src={data.staff.photoUrl} alt={data.staff.name} className="w-7 h-7 sm:w-8 sm:h-8 rounded-full object-cover border border-stone-200 flex-shrink-0" />
                                    ) : (
                                        <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-stone-200 flex items-center justify-center text-xs font-bold text-stone-500 flex-shrink-0">{data.staff.name.charAt(0).toUpperCase()}</div>
                                    )}
                                    <div className="min-w-0">
                                        <h3 className="text-xs font-bold text-stone-800 truncate">{data.staff.name}</h3>
                                        <p className="text-stone-500 text-[10px] truncate">{data.staff.role}</p>
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setSelectedStaff(selectedStaff === data.staff._id ? null : data.staff._id)}
                                    className="px-2 py-1 text-[10px] font-semibold rounded-md bg-orange-500 text-white hover:bg-orange-600 flex-shrink-0"
                                >
                                    {selectedStaff === data.staff._id ? 'Hide' : 'Details'}
                                </button>
                            </div>

                            <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5 mb-2">
                                <div className="bg-stone-50 rounded-md p-1.5 border border-stone-200">
                                    <p className="text-[10px] font-semibold uppercase text-stone-500">Orders</p>
                                    <p className="text-sm font-bold text-orange-600">{data.ordersDelivered}</p>
                                </div>
                                <div className="bg-stone-50 rounded-md p-1.5 border border-stone-200">
                                    <p className="text-[10px] font-semibold uppercase text-stone-500">Rating</p>
                                    <div className="mt-0.5">{getRatingStars(data.averageRating)}</div>
                                </div>
                                <div className="bg-stone-50 rounded-md p-1.5 border border-stone-200">
                                    <p className="text-[10px] font-semibold uppercase text-stone-500">Hours</p>
                                    <p className="text-sm font-bold text-emerald-600">{data.totalWorkingHours?.toFixed(1) ?? '0'}</p>
                                </div>
                                <div className="bg-stone-50 rounded-md p-1.5 border border-stone-200">
                                    <p className="text-[10px] font-semibold uppercase text-stone-500">Feedbacks</p>
                                    <p className="text-sm font-bold text-stone-800">{data.feedbacks?.length ?? 0}</p>
                                </div>
                            </div>

                            {selectedStaff === data.staff._id && (
                                <div className="mt-3 space-y-3 border-t border-stone-200 pt-3">
                                    {data.feedbacks?.length > 0 && (
                                        <div>
                                            <p className="text-[10px] font-semibold uppercase text-stone-500 mb-1.5">Recent feedbacks</p>
                                            <div className="space-y-2">
                                                {data.feedbacks.map((feedback, idx) => (
                                                    <div key={idx} className="bg-stone-50 rounded-lg p-2 border border-stone-200 text-xs">
                                                        <div className="flex justify-between items-start mb-0.5">
                                                            <span className="font-medium text-stone-800">{feedback.customerName || 'Anonymous'}</span>
                                                            {feedback.staffRating && <div>{getRatingStars(feedback.staffRating)}</div>}
                                                        </div>
                                                        {feedback.comment && <p className="text-stone-600 italic mt-0.5">&quot;{feedback.comment}&quot;</p>}
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                    {data.recentAttendance?.length > 0 && (
                                        <div>
                                            <p className="text-[10px] font-semibold uppercase text-stone-500 mb-1.5">Recent attendance</p>
                                            <div className="overflow-x-auto">
                                                <table className="w-full min-w-[400px] text-xs">
                                                    <thead className="bg-stone-50">
                                                        <tr>
                                                            <th className="px-2 py-1.5 text-left font-semibold text-stone-600">Date</th>
                                                            <th className="px-2 py-1.5 text-left font-semibold text-stone-600">Shift</th>
                                                            <th className="px-2 py-1.5 text-left font-semibold text-stone-600">In</th>
                                                            <th className="px-2 py-1.5 text-left font-semibold text-stone-600">Out</th>
                                                            <th className="px-2 py-1.5 text-left font-semibold text-stone-600">Hrs</th>
                                                            <th className="px-2 py-1.5 text-left font-semibold text-stone-600">Status</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {data.recentAttendance.map((attendance, idx) => (
                                                            <tr key={idx} className="border-t border-stone-100">
                                                                <td className="px-2 py-1.5 text-stone-600">{formatDate(attendance.date)}</td>
                                                                <td className="px-2 py-1.5">{attendance.shift}</td>
                                                                <td className="px-2 py-1.5 text-stone-600">{formatTime(attendance.checkInTime)}</td>
                                                                <td className="px-2 py-1.5 text-stone-600">{formatTime(attendance.checkOutTime)}</td>
                                                                <td className="px-2 py-1.5">{attendance.totalHours?.toFixed(1) || '0.0'}</td>
                                                                <td className="px-2 py-1.5">
                                                                    <span className={`px-1 py-0.5 rounded text-[10px] font-medium ${
                                                                        attendance.status === 'PRESENT' ? 'bg-emerald-100 text-emerald-800' :
                                                                        attendance.status === 'LATE' ? 'bg-amber-100 text-amber-800' :
                                                                        'bg-red-100 text-red-800'
                                                                    }`}>{attendance.status}</span>
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}

            {toast.show && <Toast message={toast.message} type={toast.type} />}
        </div>
    );
};

export default StaffPerformance;
